import { 
  doc, 
  Firestore,
  GeoPoint,
  FieldValue
} from "firebase/firestore";
import { CampLocation } from "@/types/campLocation";
import { LocationPhoto } from "@/types/campPhoto";
import { ensureFirestoreTimestamp } from "@/utils/firestoreUtils";
import { shouldForceHideFromWorkflowStatus } from "@/utils/workflowForceHide";
import { omitServerOwnedCampLocationFields } from "@/utils/serverOwnedCampLocationFields";

/**
 * Helper function to check if a value is a serverTimestamp placeholder
 */
const isServerTimestamp = (value: any): boolean => {
  // Check if it's a FieldValue (which includes serverTimestamp())
  return value && typeof value === 'object' && 
    (value instanceof FieldValue || 
     (value._methodName === 'serverTimestamp') ||
     (value._delegate && value._delegate._methodName === 'serverTimestamp'));
};

/**
 * Processes location data for storage in Firestore
 * Handles data cleaning, format conversion, and reference handling
 */
export const processLocationDataForStorage = async (
  db: Firestore,
  locationData: CampLocation
): Promise<any> => {
  // Create a clean version of the location data without firestore metadata
  const cleanedData = { ...locationData };
  delete cleanedData._form;
  
  // IMPORTANT: Remove changeDescription from the data object if it exists
  // This should only exist at the top level of the version document
  if (cleanedData.changeDescription) {
    delete cleanedData.changeDescription;
  }
  
  // Non-versioned fields: never store workflowStatus in version snapshots; persist derived forceHide on data.*
  if (Object.prototype.hasOwnProperty.call(cleanedData, "workflowStatus")) {
    const rawWs = cleanedData.workflowStatus;
    delete cleanedData.workflowStatus;
    cleanedData.forceHide = shouldForceHideFromWorkflowStatus(
      rawWs != null && rawWs !== "" ? String(rawWs) : null
    );
  }
  
  // KB-3418: rollup-owned fields are never versioned — a snapshot's copy would go stale and be written back.
  omitServerOwnedCampLocationFields(cleanedData);

  // Process latLng if it's a string to ensure it's stored as GeoPoint
  if (cleanedData.address?.latLng && typeof cleanedData.address.latLng === 'string') {
    const [lat, lng] = cleanedData.address.latLng.split(',').map(coord => parseFloat(coord.trim()));
    if (!isNaN(lat) && !isNaN(lng)) {
      cleanedData.address.latLng = new GeoPoint(lat, lng);
      
      // FIXED: Store _geoloc as GeoPoint instead of a Map with lat/lng properties
      cleanedData._geoloc = new GeoPoint(lat, lng); // Use GeoPoint instead of object
    }
  }

  // Process timestamp fields for proper storage
  if (cleanedData.createdAt) {
    cleanedData.createdAt = ensureFirestoreTimestamp(cleanedData.createdAt);
  }
  
  if (cleanedData.lastEditedAt) {
    cleanedData.lastEditedAt = ensureFirestoreTimestamp(cleanedData.lastEditedAt);
  }
  
  // Process photos array timestamps - CRITICAL FIX for photo createdAt corruption
  if (cleanedData.photos && Array.isArray(cleanedData.photos)) {
    cleanedData.photos = cleanedData.photos.map(photo => {
      if (photo && photo.createdAt !== undefined) {
        // CRITICAL: Check if createdAt is a serverTimestamp() placeholder
        // If it is, leave it alone - it will be resolved by Firestore during write
        if (isServerTimestamp(photo.createdAt)) {
          console.log('[versionDataProcessingService] Preserving serverTimestamp() placeholder for photo createdAt');
          return {
            ...photo,
            createdAt: photo.createdAt // Keep the serverTimestamp() placeholder unchanged
          };
        } else {
          // For existing timestamps, ensure they're proper Firestore Timestamps
          const processedTimestamp = ensureFirestoreTimestamp(photo.createdAt);
          return {
            ...photo,
            createdAt: processedTimestamp
          };
        }
      }
      return photo;
    }) as LocationPhoto[]; // Type assertion to fix TypeScript compatibility
  }
  
  // Clean colorScheme from variations
  if (cleanedData.variations && Array.isArray(cleanedData.variations)) {
    cleanedData.variations = cleanedData.variations.map(variation => {
      if (variation && typeof variation === 'object') {
        const cleanVariation = { ...variation };
        // Remove colorScheme completely from variations
        if ('colorScheme' in cleanVariation) {
          delete cleanVariation.colorScheme;
        }
        return cleanVariation;
      }
      return variation;
    });
  }

  // Ensure providedByOrg is a document reference
  if (cleanedData.providedByOrg && typeof cleanedData.providedByOrg === 'object' && 'id' in cleanedData.providedByOrg) {
    const orgId = cleanedData.providedByOrg.id;
    cleanedData.providedByOrg = doc(db, "campOrganizations", orgId) as any;
  }
  
  return cleanedData;
};
